import { useEffect, useMemo, useState } from 'react';
import { ROW_BASED_CONFIG, type WatchConfigTableName } from './watchSocket.constants';
import type {
  UseWatchSocketReturn,
  WatchConfigAnnouncementPayload,
  WatchConfigOptions,
  WatchMessages,
} from './watchSocket.types';
import { getConfigTableIdColumn, getConfigTableType } from './watchSocketConfigTables';

export type WatchConfigTables = Partial<Record<WatchConfigTableName, WatchConfigOptions[]>>;

const getRecordId = (table: WatchConfigTableName, options: WatchConfigOptions): unknown =>
  options.id ?? options[getConfigTableIdColumn(table)];

// Row-based single-change announcements are a patch (possibly several at once) keyed by `pk`
// (as a string, e.g. `{ "201": 50 }`) or, per the local backend's dual lookup, by `name` - never a
// full record - see hmi/local_backend/models/types.py::RowBasedConfig.__setitem__.
// Nothing to patch onto if the snapshot hasn't arrived yet; the in-flight request will supersede it.
const resolveRowBasedPatchKey = (row: WatchConfigOptions, patch: WatchConfigOptions): string | null => {
  const pkKey = row.pk !== undefined ? String(row.pk) : null;

  if (pkKey !== null && pkKey in patch) {
    return pkKey;
  }

  return typeof row.name === 'string' && row.name in patch ? row.name : null;
};

const applyRowBasedPatch = (rows: WatchConfigOptions[], patch: WatchConfigOptions): WatchConfigOptions[] =>
  rows.map((row) => {
    const key = resolveRowBasedPatchKey(row, patch);
    return key === null ? row : { ...row, val: patch[key] };
  });

const mergeAnnouncement = (
  previous: WatchConfigTables,
  { table, options }: WatchConfigAnnouncementPayload
): WatchConfigTables => {
  if (Array.isArray(options)) {
    return { ...previous, [table]: options };
  }

  const existingRows = previous[table] ?? [];

  if (getConfigTableType(table) === ROW_BASED_CONFIG) {
    return { ...previous, [table]: applyRowBasedPatch(existingRows, options) };
  }

  // A single echoed record (from this or any other client's change) - update it in place, or
  // append it if the current snapshot hasn't been fetched yet.
  const recordId = getRecordId(table, options);
  const isSameRecord = (row: WatchConfigOptions) => getRecordId(table, row) === recordId;

  return {
    ...previous,
    [table]: existingRows.some(isSameRecord)
      ? existingRows.map((row) => (isSameRecord(row) ? options : row))
      : [...existingRows, options],
  };
};

/**
 * Fetches and keeps up to date a set of watch-socket config tables. Each `requestConfig` call
 * resolves directly with its own `config/announcements` reply, which seeds `configTables`. The
 * watch also broadcasts the same announcement unsolicited whenever any client changes a table -
 * `messages` (optional) is a shared, single-slot channel (one entry per room, not per table), so
 * every broadcast is filtered against `tables` before being merged in too.
 */
export const useWatchSocketConfigTables = (
  tables: WatchConfigTableName[],
  requestConfig: UseWatchSocketReturn['requestConfig'],
  isConnected: boolean,
  messages?: WatchMessages
): WatchConfigTables => {
  const [configTables, setConfigTables] = useState<WatchConfigTables>({});

  const tablesKey = tables.join(',');
  const requestedTables = useMemo(() => tables, [tablesKey]);
  const latestAnnouncement = messages?.['config/announcements'];

  useEffect(() => {
    if (!isConnected) {
      return undefined;
    }

    let cancelled = false;

    requestedTables.forEach((table) => {
      requestConfig(table)
        .then((response) => {
          if (cancelled || response.room !== 'config/announcements') {
            return;
          }
          setConfigTables((previous) => mergeAnnouncement(previous, response.payload));
        })
        .catch((error) => {
          if (!cancelled) {
            console.error(`Failed to request watch config table "${table}"`, error);
          }
        });
    });

    return () => {
      cancelled = true;
    };
  }, [requestConfig, isConnected, requestedTables]);

  useEffect(() => {
    if (!latestAnnouncement || latestAnnouncement.room !== 'config/announcements') {
      return;
    }

    const { payload } = latestAnnouncement;

    if (!requestedTables.includes(payload.table)) {
      return;
    }

    setConfigTables((previous) => mergeAnnouncement(previous, payload));
  }, [latestAnnouncement, requestedTables]);

  return configTables;
};
